import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { LANGUAGE_OPTIONS, VideoSubtitleType } from 'type/videoSubtitleType';

interface PreviewProps {
  videoUrl: string;
  subtitles?: VideoSubtitleType[];
}

interface TrackData {
  kind: 'subtitles';
  src: string;
  srcLang: string;
  label: string;
  default: boolean;
}

const Preview: React.FC<PreviewProps> = ({ videoUrl, subtitles }: PreviewProps) => {
  console.log('videoUrl in preview', videoUrl);
  console.log('subtitles in preview', subtitles);

  const playerRef = useRef<any>(null);
  const blobUrlsRef = useRef<Map<string, string>>(new Map());
  const [trackUrls, setTrackUrls] = useState<Map<string, string>>(new Map());

  // Convert subtitles to track data
  const tracks = useMemo(() => {
    if (!subtitles || subtitles.length === 0) return [];

    return subtitles
      .filter(subtitle => {
        // Only include subtitles with valid URLs
        const url = subtitle.subtitle_url || subtitle.file_url || subtitle.external_url;
        return url && typeof url === 'string';
      })
      .map(subtitle => {
        const langInfo = LANGUAGE_OPTIONS.find(lang => lang.value === subtitle.lang) || {
          value: subtitle.lang,
          label: subtitle.lang.toUpperCase(),
        };

        return {
          kind: 'subtitles' as const,
          src: subtitle.subtitle_url || subtitle.file_url || subtitle.external_url || '',
          srcLang: subtitle.lang,
          label: langInfo.label,
          default: subtitle.is_integrated || false,
        };
      });
  }, [subtitles]);

  // Fetch subtitle files to bypass CORS issues and create blob URLs
  useEffect(() => {
    if (tracks.length === 0) {
      // Cleanup old blob URLs
      blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      blobUrlsRef.current.clear();
      setTrackUrls(new Map());
      return;
    }

    const fetchSubtitles = async () => {
      const newTrackUrls = new Map<string, string>();

      for (const trackData of tracks) {
        const originalSrc = trackData.src;
        try {
          // Fetch subtitle file - this bypasses CORS issues
          const response = await fetch(originalSrc, {
            method: 'GET',
            mode: 'cors', // Explicitly use CORS mode
            credentials: 'omit', // Don't send credentials for public files
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch subtitle: ${response.status} ${response.statusText}`);
          }

          // Get the blob and create a blob URL
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          blobUrlsRef.current.set(originalSrc, blobUrl);
          newTrackUrls.set(originalSrc, blobUrl);

          console.log('✅ Subtitle fetched and blob URL created:', {
            originalSrc,
            blobUrl,
            lang: trackData.srcLang,
          });
        } catch (error) {
          console.error('Failed to fetch subtitle, using original URL:', {
            src: originalSrc,
            lang: trackData.srcLang,
            error,
          });
          // Use original URL if fetch fails (may still work if CORS is configured)
          newTrackUrls.set(originalSrc, originalSrc);
        }
      }

      setTrackUrls(newTrackUrls);

      // Cleanup old blob URLs that are no longer needed
      const currentSrcs = new Set(tracks.map(t => t.src));
      blobUrlsRef.current.forEach((url, src) => {
        if (!currentSrcs.has(src)) {
          URL.revokeObjectURL(url);
          blobUrlsRef.current.delete(src);
        }
      });
    };

    fetchSubtitles();

    // Cleanup on unmount
    return () => {
      blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      blobUrlsRef.current.clear();
    };
  }, [tracks]);

  // Create tracks with blob URLs
  const tracksWithUrls = useMemo<TrackData[]>(() => {
    return tracks.map(trackData => {
      const blobUrl = trackUrls.get(trackData.src);
      return {
        ...trackData,
        src: blobUrl || trackData.src, // Use blob URL if available, otherwise original
      };
    });
  }, [tracks, trackUrls]);

  // Add tracks to video element after ReactPlayer renders
  useEffect(() => {
    if (tracksWithUrls.length === 0 || !playerRef.current) return;

    const addTracks = () => {
      // Try to get the internal player (video element)
      const internalPlayer = playerRef.current?.getInternalPlayer?.() as HTMLVideoElement | null;
      if (!internalPlayer) {
        // If not available, try again after a short delay
        setTimeout(addTracks, 100);
        return;
      }

      // Remove existing track elements
      const existingTracks = internalPlayer.querySelectorAll('track');
      existingTracks.forEach(track => track.remove());

      // Add new track elements with error handling
      tracksWithUrls.forEach(trackData => {
        const trackElement = document.createElement('track');
        trackElement.kind = trackData.kind;
        trackElement.src = trackData.src;
        trackElement.srclang = trackData.srcLang;
        trackElement.label = trackData.label;
        trackElement.default = trackData.default;

        // Add error handler for CORS or loading issues
        trackElement.addEventListener('error', e => {
          console.error('Subtitle track error:', {
            src: trackData.src,
            lang: trackData.srcLang,
            label: trackData.label,
            error: e,
            message: 'Failed to load subtitle. This may be due to CORS policy or invalid URL.',
          });
        });

        // Add load handler for successful loading
        trackElement.addEventListener('load', () => {
          console.log('Subtitle track loaded successfully:', {
            src: trackData.src,
            lang: trackData.srcLang,
            label: trackData.label,
          });

          // Enable the track if it's set as default
          if (trackData.default && internalPlayer.textTracks) {
            for (let i = 0; i < internalPlayer.textTracks.length; i++) {
              const textTrack = internalPlayer.textTracks[i];
              if (textTrack.language === trackData.srcLang && textTrack.kind === 'subtitles') {
                textTrack.mode = 'showing';
                break;
              }
            }
          }
        });

        internalPlayer.appendChild(trackElement);
      });

      // Monitor textTracks for changes
      if (internalPlayer.textTracks) {
        internalPlayer.textTracks.addEventListener('addtrack', event => {
          const track = event.track as TextTrack;
          if (track.kind === 'subtitles') {
            track.addEventListener('error', e => {
              console.error('TextTrack error:', {
                label: track.label,
                language: track.language,
                kind: track.kind,
                error: e,
                message: 'CORS error: The subtitle file may not have proper CORS headers.',
              });
            });
          }
        });

        // Check if tracks are loaded after a delay
        setTimeout(() => {
          if (internalPlayer.textTracks) {
            const loadedTracks: string[] = [];
            const emptyTracks: string[] = [];

            for (let i = 0; i < internalPlayer.textTracks.length; i++) {
              const track = internalPlayer.textTracks[i];
              if (track.kind === 'subtitles') {
                // Check track state
                if (track.cues && track.cues.length > 0) {
                  loadedTracks.push(`${track.label} (${track.language})`);
                } else {
                  // Track exists but has no cues - might be loading or failed
                  emptyTracks.push(`${track.label} (${track.language})`);
                }
              }
            }

            if (loadedTracks.length > 0) {
              console.log('✅ Loaded subtitle tracks:', loadedTracks);
            }
            if (emptyTracks.length > 0) {
              console.warn(
                '⚠️ Subtitle tracks without cues (may be loading or failed):',
                emptyTracks
              );
              console.warn(
                '⚠️ If subtitles do not appear, check:\n' +
                  '1. CORS headers on CloudFront (Access-Control-Allow-Origin)\n' +
                  '2. Subtitle file format (must be WebVTT or SRT)\n' +
                  '3. Browser console for CORS errors'
              );
            }
          }
        }, 1000);
      }
    };

    // Wait for ReactPlayer to initialize
    setTimeout(addTracks, 200);
  }, [tracksWithUrls, videoUrl]);

  const player = useMemo(
    () => (
      <ReactPlayer
        ref={playerRef}
        url={videoUrl}
        controls
        width="100%"
        height="auto"
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
            },
          },
        }}
      />
    ),
    [videoUrl]
  );

  return <div className="preview-container">{videoUrl ? player : <p>There is no video...</p>}</div>;
};

export default Preview;
